// src/services/appointments.service.ts
import { useApiClientStore } from '../stores/useApiClientStore';

import { ApiResponseRaw, PatientDetails } from '../types';
import {
  AppointmentCancellationResponse,
  AppointmentFilters,
  AppointmentResponse,
  BookAppointmentPayload,
  BookAppointmentResponse,
  BookingLinkResponse,
  SlotsResponse,
} from '../types/appointment';

// `/slots` and `/book` read the patient from the session cookie `/authenticate`
// mints, so the link must be authenticated before either is called.

const cancellationPath = (appointmentId: string) =>
  `/patient-portal/appointment/${appointmentId}/cancellation`;

const appointmentPath = (appointmentId: string) => `/patient-portal/appointment/${appointmentId}`;

const bookingLinkPath = (bookingLinkId: string, suffix = '') =>
  `/patient-portal/booking-link/${bookingLinkId}${suffix}`;

/** `getRaw`: an expired or already-cancelled link is a state to render, not a throw. */
export async function fetchAppointmentCancellationApi(
  appointmentId: string,
): Promise<ApiResponseRaw<AppointmentCancellationResponse>> {
  const api = useApiClientStore().getClient();

  return api.getRaw<AppointmentCancellationResponse>(cancellationPath(appointmentId));
}

export async function cancelAppointmentApi(appointmentId: string): Promise<ApiResponseRaw<null>> {
  const api = useApiClientStore().getClient();

  return api.postRaw<null>(cancellationPath(appointmentId), {});
}

/** The booked appointment's summary — `POST /book` returns only the id. */
export async function fetchAppointmentApi(
  appointmentId: string,
): Promise<ApiResponseRaw<AppointmentResponse>> {
  const api = useApiClientStore().getClient();

  return api.getRaw<AppointmentResponse>(appointmentPath(appointmentId));
}

export async function fetchBookingLinkApi(
  bookingLinkId: string,
): Promise<ApiResponseRaw<BookingLinkResponse>> {
  const api = useApiClientStore().getClient();

  return api.getRaw<BookingLinkResponse>(bookingLinkPath(bookingLinkId));
}

/** Three-point match on name, DOB and postcode; sets the session cookie. */
export async function authenticateBookingLinkApi(
  bookingLinkId: string,
  patientDetails: PatientDetails,
): Promise<ApiResponseRaw<null>> {
  const api = useApiClientStore().getClient();

  return api.postRaw<null>(bookingLinkPath(bookingLinkId, '/authenticate'), patientDetails);
}

/** Filters and the window are query params, so the slot list stays a plain GET. */
export async function fetchSlotsApi(
  bookingLinkId: string,
  fromDate: string | null,
  filters: AppointmentFilters,
): Promise<ApiResponseRaw<SlotsResponse>> {
  const api = useApiClientStore().getClient();

  const query = new URLSearchParams();
  if (fromDate) query.set('fromDate', fromDate);
  if (filters.preferredPractitioner) query.set('practitioner', filters.preferredPractitioner);
  if (filters.preferredSite) query.set('site', filters.preferredSite);
  for (const gender of filters.preferredPractitionerGenders) {
    query.append('practitionerGenders[]', gender);
  }

  const suffix = query.toString() ? `/slots?${query}` : '/slots';

  return api.getRaw<SlotsResponse>(bookingLinkPath(bookingLinkId, suffix));
}

/** Selecting a slot books it; `slot-no-longer-available` comes back inline. */
export async function bookAppointmentApi(
  bookingLinkId: string,
  payload: BookAppointmentPayload,
): Promise<ApiResponseRaw<BookAppointmentResponse>> {
  const api = useApiClientStore().getClient();

  return api.postRaw<BookAppointmentResponse>(bookingLinkPath(bookingLinkId, '/book'), payload);
}
